import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui';
import { toast } from '../components/ui/Toaster';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { userService } from '../services/userService';
import AuthLayout from '../layouts/AuthLayout';
import { consumePostAuthRedirect, peekPostAuthRedirect } from '../utils/postAuthRedirect';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuthStore();
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      handleVerification(token);
    }
  }, [token]);

  const handleVerification = async (verificationToken: string) => {
    setVerifying(true);
    setError(null);

    try {
      await api.post('/auth/verify-email', { token: verificationToken });
      setVerified(true);
      setError(null); // Clear any error when verification succeeds
      
      // Only try to update user profile if user is logged in
      const { user: currentUser } = useAuthStore.getState();
      if (currentUser) {
        try {
          const profileData = await userService.getUserProfile();
          // Update the entire user object with fresh data from server
          updateUser(profileData);
        } catch (profileError) {
          // If profile fetch fails, at least update the verification status
          updateUser({ 
            emailVerified: true,
            emailVerifiedAt: new Date().toISOString()
          });
        }
      }
      
      toast.success('E-mail succesvol geverifieerd! Welkom bij SoloFix!');
      
      // Check if user is logged in - if so, redirect to their intended target
      const { user: verifiedUser } = useAuthStore.getState();
      if (verifiedUser) {
        // User is logged in: send them straight to the destination they
        // originally tried to reach (e.g. a shared group link), else dashboard.
        const redirectTo = consumePostAuthRedirect() || '/dashboard';
        setTimeout(() => {
          navigate(redirectTo);
        }, 2000);
      } else {
        // User not logged in: they must sign in first. Do NOT consume the stored
        // redirect here — leave it so LoginPage can pick it up and finish the
        // journey to the intended destination after login.
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'E-mail succesvol geverifieerd! Je kunt nu inloggen.',
              from: peekPostAuthRedirect() || undefined,
            }
          });
        }, 3000);
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Verificatie mislukt';
      setError(message);
      setVerified(false); // Clear verified state when there's an error
      toast.error(message);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    const email = localStorage.getItem('pendingVerificationEmail');
    
    if (!email) {
      toast.error('E-mailadres niet gevonden. Probeer opnieuw in te loggen.');
      navigate('/login');
      return;
    }

    setResending(true);
    
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('Verificatie-e-mail opnieuw verzonden. Controleer je inbox.');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Verzenden mislukt';
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div className="text-center space-y-6">
          <div className="w-16 h-16 mx-auto bg-neon-gradient rounded-full flex items-center justify-center shadow-neon-pink">
            <Mail className="h-8 w-8 text-ink" />
          </div>

          <div>
            <div className="sf-eyebrow mb-3">// e-mail verificatie</div>
            <h1 className="text-2xl font-bold text-fg-bright tracking-tight">Verificeer je e-mailadres</h1>
            <p className="mt-2 text-fg-muted">
              We hebben een verificatie-e-mail naar je verzonden. Klik op de link in de e-mail om je account te activeren.
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-fg-muted">
              E-mail niet ontvangen? Controleer je spam-map of vraag een nieuwe aan.
            </p>

            <Button
              onClick={handleResendVerification}
              disabled={resending}
              variant="outline"
              className="w-full"
            >
              {resending ? 'Verzenden...' : 'Verificatie-e-mail opnieuw verzenden'}
            </Button>
          </div>

          <div className="pt-4">
            <Button
              onClick={() => navigate('/login')}
              variant="ghost"
              className="w-full"
            >
              Terug naar inloggen
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center space-y-6">
        {verifying && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-white/[.06] border border-white/10 rounded-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-cyan"></div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-fg-bright tracking-tight">E-mail verifiëren...</h1>
              <p className="mt-2 text-fg-muted">Een moment geduld terwijl we je e-mailadres verifiëren.</p>
            </div>
          </div>
        )}

        {verified && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-neon-lime/[.08] border border-neon-lime/25 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-neon-lime" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-fg-bright tracking-tight">E-mail geverifieerd!</h1>
              <p className="mt-2 text-fg-muted">
                Welkom bij SoloFix! Je wordt doorgestuurd...
              </p>
            </div>
          </div>
        )}

        {error && !verifying && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-neon-pink/[.08] border border-neon-pink/30 rounded-full flex items-center justify-center">
              <XCircle className="h-8 w-8 text-neon-pink" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-fg-bright tracking-tight">Verificatie mislukt</h1>
              <p className="mt-2 text-fg-muted">{error}</p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full py-3.5"
              >
                {resending ? 'Verzenden...' : 'Nieuwe verificatie-e-mail aanvragen'}
              </Button>

              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Terug naar inloggen
              </Button>
            </div>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};